import {
  ApolloClient,
  ApolloLink,
  from,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import type { GraphQLFormattedError } from 'graphql';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { environment } from 'environments/environment';
import { isPlaywrightTestMode } from 'lib/test-mode';
import { ALLOWED_OPERATIONS } from './constants';

export async function getApolloClient(
  getToken: () => string,
  onErrorCallback?: (error: {
    graphQLErrors?: readonly GraphQLFormattedError[];
    networkError?: any;
  }) => void,
  getRouteInfo?: () => { pathname: string; routePattern?: string },
  impersonateUserId?: string | null,
  authCallbacks?: {
    checkTokenExpiry?: () => boolean;
    refreshToken?: () => Promise<string>;
  },
): Promise<ApolloClient<NormalizedCacheObject>> {
  const isTestMode = isPlaywrightTestMode();

  const authMiddleware = new ApolloLink((operation, forward) => {
    // Read the current token on each request so background token refreshes
    // don't require recreating the Apollo client.
    const token = getToken();

    // Check token expiry before making request
    if (
      token &&
      authCallbacks?.checkTokenExpiry &&
      authCallbacks?.refreshToken
    ) {
      const isExpired = authCallbacks.checkTokenExpiry();

      if (isExpired) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Apollo] Token expired before request, triggering refresh',
        );
        // Note: Token refresh is handled by CognitoAuthContext periodic check
        // and will trigger Apollo client recreation in AuthApolloProvider
        // For immediate requests, the periodic check should catch this
      }
    }

    const headers: Record<string, string> = {};

    if (token) {
      headers.authorization = `Bearer ${token}`;
    } else if (
      !operation.operationName ||
      !ALLOWED_OPERATIONS.includes(
        operation.operationName as (typeof ALLOWED_OPERATIONS)[number],
      )
    ) {
      throw new Error(
        `No access token available for operation ${operation.operationName}`,
      );
    }

    // Add referrer headers for tracking — read dynamically so headers stay
    // current without recreating the Apollo client on every navigation.
    const routeInfo = getRouteInfo?.();
    if (routeInfo) {
      headers['x-referrer-path'] = routeInfo.pathname;
      if (routeInfo.routePattern) {
        headers['x-referrer-route'] = routeInfo.routePattern;
      }
    } else if (typeof window !== 'undefined') {
      // Fallback to window location if route info not provided
      headers['x-referrer-path'] = window.location.pathname;
    }

    // Add impersonation header if provided
    if (impersonateUserId) {
      headers['x-impersonate-user-id'] = impersonateUserId;
    }

    // In test mode, inject x-test-user so the API can identify the seeded user.
    // Prefer the `test_user` the Playwright fixture wrote to localStorage so we
    // can't drift from what the seeder created. Fall back to a hardcoded table
    // for non-Playwright dev runs (yarn faker:dev).
    if (isTestMode) {
      type TestUserPayload = {
        sub: string;
        email: string;
        name: string;
        'https://farmshare.app/role': string;
      };

      let testUser: TestUserPayload | undefined;

      const storedTestUser =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('test_user')
          : null;

      if (storedTestUser) {
        try {
          testUser = JSON.parse(storedTestUser) as TestUserPayload;
        } catch {
          // fall through to hardcoded table
        }
      }

      if (!testUser) {
        const TEST_USERS: Record<string, TestUserPayload> = {
          processor: {
            sub: 'auth0|test_processor',
            email: 'processor@testprocessor.com',
            name: 'Test Processor User',
            'https://farmshare.app/role': 'processor',
          },
          admin: {
            sub: 'auth0|test_admin',
            email: 'admin@testprocessor.com',
            name: 'Test Admin',
            'https://farmshare.app/role': 'admin',
          },
          producer: {
            sub: 'auth0|test_producer',
            email: 'producer@testproducer.com',
            name: 'Test Producer User',
            'https://farmshare.app/role': 'producer',
          },
          buyer: {
            sub: 'auth0|test_buyer',
            email: 'buyer@testbuyer.com',
            name: 'Test Buyer User',
            'https://farmshare.app/role': 'buyer',
          },
        };
        const role = import.meta.env.VITE_TEST_USER_ROLE || 'processor';
        testUser = TEST_USERS[role] || TEST_USERS.processor;
      }

      headers['x-test-user'] = JSON.stringify(testUser);
      headers['playwright-test-mode'] = 'true';
    }

    operation.setContext({
      headers,
    });

    return forward(operation);
  });

  const cache = new InMemoryCache({
    typePolicies: {
      Tenant: {
        fields: {
          vendors: {
            // Merge vendor arrays by combining refs and merging matching objects
            merge(existing, incoming, { mergeObjects }) {
              // If no existing data, just use incoming
              if (!existing) return incoming;
              // If no incoming data, keep existing
              if (!incoming) return existing;

              // Create a map of existing refs for quick lookup
              const existingMap = new Map(
                existing.map((ref: any) => [ref.__ref, ref]),
              );

              // Merge incoming refs with existing
              incoming.forEach((incomingRef: any) => {
                const existingRef = existingMap.get(incomingRef.__ref);
                if (existingRef) {
                  // Merge the objects if both exist
                  existingMap.set(
                    incomingRef.__ref,
                    mergeObjects(existingRef, incomingRef),
                  );
                } else {
                  // Add new ref that doesn't exist
                  existingMap.set(incomingRef.__ref, incomingRef);
                }
              });

              return Array.from(existingMap.values());
            },
          },
        },
      },
      ProcessorCapabilitySpecification: {
        fields: {
          specifications: {
            // Don't merge specifications arrays - keep them separate per parent
            merge: false,
          },
        },
      },
      ProcessorCapabilitySpecificationSpecifications: {
        // Disable normalization to prevent cache key collisions
        keyFields: false,
        fields: {
          // Don't merge options arrays - keep them separate per parent specification
          options: {
            merge: false,
          },
        },
      },
      ProcessorCapabilitySpecificationSpecificationsOptions: {
        // Disable normalization to prevent cache key collisions for options too
        keyFields: false,
        fields: {
          options: {
            merge: false,
          },
        },
      },
    },
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    // Check for 401 Unauthorized errors
    const is401Error =
      (networkError &&
        'statusCode' in networkError &&
        networkError.statusCode === 401) ||
      graphQLErrors?.some((error) =>
        error.message.toLowerCase().includes('unauthorized'),
      );

    if (is401Error) {
      // eslint-disable-next-line no-console
      console.warn(
        '[Apollo] 401 Unauthorized error detected',
        operation.operationName,
      );

      // Check if token is expired and attempt refresh if callbacks provided
      if (authCallbacks?.checkTokenExpiry && authCallbacks?.refreshToken) {
        const isExpired = authCallbacks.checkTokenExpiry();

        if (isExpired) {
          // eslint-disable-next-line no-console
          console.log('[Apollo] Token expired, refresh should be triggered');
          // Note: The actual refresh and retry logic is handled at the application level
          // via the CognitoAuthContext's periodic token check and the AuthApolloProvider
        }
      }
    }

    // Separate schema-drift errors (field not recognised by current server version)
    // from genuine application errors. Drift errors are expected during rolling
    // deployments and should not trigger the error callback or disrupt the UI;
    // partial data returned alongside them is still usable thanks to errorPolicy:'all'.
    const schemaDriftErrors = graphQLErrors?.filter((e) =>
      e.message.startsWith('Cannot query field'),
    );
    const fatalErrors = graphQLErrors?.filter(
      (e) => !e.message.startsWith('Cannot query field'),
    );

    const isMutation = operation.query.definitions.some(
      (def) =>
        def.kind === 'OperationDefinition' &&
        (def as any).operation === 'mutation',
    );

    // A caller that recovers from network failures itself (e.g. the data
    // import's finalize, which waits for the server after a 504) opts out of
    // the global toast for them with `context: { handlesNetworkErrors: true }`.
    const reportNetworkError =
      !!networkError && !operation.getContext().handlesNetworkErrors;

    // Call the custom error callback only for mutations with non-drift errors
    if (
      isMutation &&
      onErrorCallback &&
      (fatalErrors?.length || reportNetworkError)
    ) {
      onErrorCallback({
        graphQLErrors: fatalErrors,
        networkError: reportNetworkError ? networkError : undefined,
      });
    }

    if (schemaDriftErrors?.length) {
      schemaDriftErrors.forEach(({ message, path }) =>
        // eslint-disable-next-line no-console
        console.warn(
          `[GraphQL schema drift]: ${message}, Path: ${path} — partial data returned`,
        ),
      );
    }

    if (fatalErrors?.length) {
      fatalErrors.forEach(({ message, locations, path }) =>
        // eslint-disable-next-line no-console
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    }

    if (networkError) {
      // eslint-disable-next-line no-console
      console.error(`[Network error]: ${networkError}`);
    }
  });

  // Debug link to log all network requests and cache operations
  const debugLink = new ApolloLink((operation, forward) => {
    if (!import.meta.env.VITE_ENABLE_DETAILED_APOLLO_LOGGING) {
      return forward(operation);
    }
    // eslint-disable-next-line no-console
    console.log('[Apollo Network] Starting request:', {
      operationName: operation.operationName,
      variables: operation.variables,
      timestamp: new Date().toISOString(),
    });

    return forward(operation).map((response) => {
      // eslint-disable-next-line no-console
      console.log('[Apollo Network] Received response:', {
        operationName: operation.operationName,
        response,
        timestamp: new Date().toISOString(),
      });
      return response;
    });
  });

  const httpLink = createUploadLink({
    uri: `${import.meta.env?.VITE_GRAPHQL_URL || '/'}api/graphql`,
    headers: {
      'keep-alive': 'true',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
  });

  return new ApolloClient({
    link: from([
      errorLink,
      debugLink,
      authMiddleware,
      httpLink as unknown as ApolloLink,
    ]),
    cache,
    credentials: 'same-origin',
    // Everywhere but the production stage (dev and staging are production
    // builds too, so `import.meta.env.PROD` can't tell them apart).
    connectToDevTools: environment.env !== 'production',
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
    },
  });
}
